/**
 * VideoPlayer — Reproductor unificado usado en Biblioteca y Rutina.
 *
 * Cascada de prioridad (misma lógica que antes vivía duplicada):
 *   1. Sirv / Cloudinary → <video> HTML5 nativo (mp4 fast-start + byte-range)
 *   2. iOS + Drive       → proxy backend /api/videos/proxy-drive/{id}
 *   3. YouTube / Drive desktop → iframe
 *   4. cualquier otro    → <video> HTML5 directo
 *
 * Props:
 *   - url            (string, required)  URL del video
 *   - source         (string, optional)  'youtube' | 'drive' | 'sirv' | 'cloudinary' | otro
 *   - title          (string, optional)  título para el <video title="…">
 *   - className      (string, optional)  clases extras para el contenedor
 *   - onClose        (fn, optional)      callback para cerrar el modal padre
 */
import PropTypes from 'prop-types';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ═════ HELPERS COMPARTIDOS ═════
const getEmbedUrl = (url, source) => {
  if (!url) return null;
  if (source === 'youtube' || url.includes('youtube.com') || url.includes('youtu.be')) {
    let videoId = '';
    if (url.includes('youtube.com/watch')) {
      const params = new URLSearchParams(url.split('?')[1]);
      videoId = params.get('v');
    } else if (url.includes('youtu.be/')) {
      videoId = url.split('youtu.be/')[1]?.split('?')[0];
    } else if (url.includes('youtube.com/embed/')) {
      videoId = url.split('embed/')[1]?.split('?')[0];
    }
    if (videoId) return `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`;
  }
  if (source === 'drive' || url.includes('drive.google.com')) {
    let fileId = '';
    if (url.includes('/file/d/')) fileId = url.split('/file/d/')[1]?.split('/')[0]?.split('?')[0];
    else if (url.includes('id=')) fileId = url.split('id=')[1]?.split('&')[0];
    if (fileId) return `https://drive.google.com/file/d/${fileId}/preview`;
  }
  return url;
};

const getDriveFileId = (url) => {
  if (!url || !url.includes('drive.google.com')) return null;
  if (url.includes('/file/d/')) return url.split('/file/d/')[1]?.split('/')[0]?.split('?')[0] || null;
  if (url.includes('id=')) return url.split('id=')[1]?.split('&')[0] || null;
  return null;
};

const isCloudinaryUrl = (url) => !!url && /res\.cloudinary\.com\/.+\/video\/upload\//i.test(url);
const isSirvUrl = (url) => !!url && /\.sirv\.com\//i.test(url);
// 2026-02 · Bunny Stream / Bunny CDN
// - Pull zone directo (`*.b-cdn.net`) → <video> HTML5 nativo (mp4 directo).
// - Player embed (`player.mediadelivery.net/play/{lib}/{id}` o
//   `iframe.mediadelivery.net/(play|embed)/{lib}/{id}`) → <iframe> (Bunny
//   Stream sirve el player HTML con DRM/tokens desde ese endpoint).
const isBunnyDirectUrl = (url) => !!url && /\.b-cdn\.net\//i.test(url);
const isBunnyPlayerUrl = (url) => !!url && /(player|iframe)\.mediadelivery\.net\/(play|embed)\//i.test(url);
const isBunnyUrl = (url) => isBunnyDirectUrl(url) || isBunnyPlayerUrl(url);

const needsIframe = (url, source) =>
  source === 'youtube' || source === 'drive' ||
  url?.includes('youtube.com') || url?.includes('youtu.be') || url?.includes('drive.google.com') ||
  isBunnyPlayerUrl(url);

const isIOSDevice = () => {
  if (typeof window === 'undefined') return false;
  const ua = window.navigator.userAgent || '';
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isIPadOS = ua.includes('Mac') && 'ontouchend' in document;
  return isIOS || isIPadOS;
};

const commonVideoProps = {
  controls: true,
  autoPlay: true,
  playsInline: true,
  preload: 'metadata',
  disablePictureInPicture: true,
  controlsList: 'nodownload',
};

export function VideoPlayer({ url, source, title, className = '' }) {
  if (!url) {
    return (
      <div className={`absolute inset-0 flex items-center justify-center bg-black text-white/60 ${className}`}>
        Sin video disponible
      </div>
    );
  }
  const isSirv = isSirvUrl(url);
  const isCloudinary = isCloudinaryUrl(url);
  const isBunnyDirect = isBunnyDirectUrl(url);
  const isBunnyPlayer = isBunnyPlayerUrl(url) || source === 'bunny_player';
  const isDrive = url.includes('drive.google.com');
  const iosMode = isIOSDevice();
  const driveFileId = isDrive ? getDriveFileId(url) : null;
  const embedUrl = getEmbedUrl(url, source);
  const useIframe = needsIframe(url, source);

  // 1) Sirv / Cloudinary / Bunny (direct mp4) — HTML5 nativo
  if (isSirv || isCloudinary || isBunnyDirect) {
    return (
      <video
        {...commonVideoProps}
        src={url}
        title={title}
        className={`absolute inset-0 w-full h-full object-contain bg-black ${className}`}
        data-testid={isSirv ? 'sirv-video-player' : isBunnyDirect ? 'bunny-video-player' : 'cloudinary-video-player'}
      >
        Tu navegador no soporta el elemento de video.
      </video>
    );
  }

  // 1.b) Bunny Stream player embed — iframe (player.mediadelivery.net)
  if (isBunnyPlayer) {
    return (
      <iframe
        src={url}
        title={title}
        className={`absolute inset-0 w-full h-full ${className}`}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
        data-testid="bunny-iframe-player"
      />
    );
  }

  // 2) iOS + Drive — usar proxy backend con Range/mp4
  if (isDrive && iosMode && driveFileId) {
    return (
      <video
        {...commonVideoProps}
        src={`${API}/videos/proxy-drive/${driveFileId}`}
        title={title}
        className={`absolute inset-0 w-full h-full object-contain bg-black ${className}`}
        data-testid="ios-drive-video-player"
      >
        Tu navegador no soporta el elemento de video.
      </video>
    );
  }

  // 3) iframe (YouTube o Drive desktop)
  if (useIframe) {
    return (
      <iframe
        src={embedUrl}
        title={title}
        className={`absolute inset-0 w-full h-full ${className}`}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; web-share"
        allowFullScreen
      />
    );
  }

  // 4) Video directo
  return (
    <video
      {...commonVideoProps}
      src={embedUrl || url}
      title={title}
      className={`absolute inset-0 w-full h-full object-contain bg-black ${className}`}
    >
      Tu navegador no soporta el elemento de video.
    </video>
  );
}

VideoPlayer.propTypes = {
  url: PropTypes.string,
  source: PropTypes.string,
  title: PropTypes.string,
  className: PropTypes.string,
};

export default VideoPlayer;
